import React, { ComponentType, useEffect, useReducer } from 'react';
import isNull from 'lodash/isNull';
import { renderNothing, resolveComponent } from './utils';
import { LoadableComponentType, LoaderResult, ReactLoadableProps } from './types';
import { useMounted } from '../../hooks/useMounted';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ReactLoadable = <Component extends ComponentType<React.PropsWithChildren<any>>>({
	loading = renderNothing,
	beforeLoad,
	loader,
}: ReactLoadableProps<Component>): LoadableComponentType<Component> => {
	let loaderPromise: Promise<LoaderResult<Component>> | null = null;
	let loaded: LoaderResult<Component> | null = null;
	let error: string | null = null;

	const load = async () => {
		beforeLoad?.();
		loaderPromise = loader();
		// await new Promise(() => {});
		loaderPromise
			.then((result) => {
				loaded = result;
			})
			.catch((e) => {
				console.log(e);
				error = 'Could not load module';
				throw e;
			});
	};

	const tryToLoad = () => {
		if (loaderPromise) return;

		load();
	};

	const LoadableComponent: LoadableComponentType<Component> = (props) => {
		const { mountedRef } = useMounted();
		const [, forceUpdate] = useReducer((c) => c + 1, 0);

		useEffect(() => {
			if (loaded) return;

			tryToLoad();

			loaderPromise!.finally(() => {
				if (mountedRef.current) {
					forceUpdate();
				}
			});
		}, [mountedRef]);

		if (isNull(loaded)) {
			return React.createElement(loading, { ...props, error });
		} else {
			const component = resolveComponent(loaded);

			return React.createElement(component, props);
		}
	};

	LoadableComponent.preload = tryToLoad;

	return LoadableComponent;
};

export default ReactLoadable;
